import { delay, map, share } from 'rxjs/operators';
import { Injectable, NgZone } from '@angular/core';
import { concat, interval, MonoTypeOperatorFunction, Observable, of, of as observableOf, SchedulerLike as IScheduler } from 'rxjs';
import moment from 'moment';

@Injectable({ providedIn: 'root' })
export class BackgroundTaskFactory {
  private readonly watchesPeriod = moment.duration(1, 'second');

  private savedShifts: number = 0;
  private serverTimeShift: number = 0;
  private getNowDelegate: () => moment.Moment;
  private serverWatches$: Observable<moment.Moment>;

  public constructor(private readonly zone: NgZone) {}

  public getServerWatches(scheduler?: IScheduler): Observable<moment.Moment> {
    if (!this.serverWatches$) {
      const now = this.getServerTime();

      const nextIntegerSecondDelay = moment(now).add(1, 'second').milliseconds(0).diff(now, 'milliseconds');

      this.serverWatches$ = concat(
        of(undefined),
        of(undefined).pipe(delay(nextIntegerSecondDelay, scheduler)),
        interval(this.watchesPeriod.asMilliseconds(), scheduler),
      ).pipe(
        map(() => this.getServerTime()),
        this.toForeground(),
        share(),
      );
    }

    return this.serverWatches$;
  }

  public setGetNow(getNow: () => moment.Moment): void {
    this.getNowDelegate = getNow;
  }

  public setServerTime(serverTime: moment.Moment, clientTime: moment.Moment): void {
    const serverTimeShift = serverTime.diff(clientTime, 'milliseconds');
    this.savedShifts++;
    this.serverTimeShift = (this.serverTimeShift * (this.savedShifts - 1) + serverTimeShift) / this.savedShifts;
  }

  public toBackground<TItem>(): MonoTypeOperatorFunction<TItem> {
    return (observable$) =>
      this.zone.runOutsideAngular(
        () =>
          new Observable<TItem>((observer$) =>
            observable$.subscribe({
              next: (value) => this.zone.runOutsideAngular(() => observer$.next(value)),
              error: (error) => this.zone.runOutsideAngular(() => observer$.error(error)),
              complete: () => this.zone.runOutsideAngular(() => observer$.complete()),
            }),
          ),
      );
  }

  public toForeground<TItem>(): MonoTypeOperatorFunction<TItem> {
    return (observable$) =>
      new Observable<TItem>((observer$) =>
        observable$.subscribe({
          next: (value) => this.zone.run(() => observer$.next(value)),
          error: (error) => this.zone.run(() => observer$.error(error)),
          complete: () => this.zone.run(() => observer$.complete()),
        }),
      );
  }

  /*
   * Creates interval task which is running outside angular and marshalles back results.
   * @param interval {number} interval of timer.
   * @param schduler? {IScheduler}
   * @returns {Observable<TModel>} foreground observable
   */
  public createIntervalTask(inter: number, scheduler?: IScheduler): Observable<number> {
    return interval(inter, scheduler);
  }

  /*
   * Creates timer task which is running outside angular and marshalles back results.
   * @param interval {number} of timer.
   * @param schduler? {IScheduler}
   * @returns {Observable<TModel>} foreground observable
   */
  public createTimerTask(inter: number, scheduler?: IScheduler): Observable<number> {
    return concat(of(0), interval(inter, scheduler).pipe(map((i) => i + 1)));
  }

  /*
   * Creates delay task which is running outside angular and marshalles back result after delay complete.
   * @param delay {number} delay of timeout.
   * @param schduler? {IScheduler}
   * @returns {Observable<TModel>} foreground observable
   */
  public createDelayTask(del: number, scheduler?: IScheduler): Observable<number> {
    return observableOf(undefined).pipe(delay(del, scheduler));
  }

  private getServerTime(): moment.Moment {
    return this.getNow().add(this.serverTimeShift, 'milliseconds');
  }

  private getNow(): moment.Moment {
    const getNow = this.getNowDelegate;
    return getNow ? getNow() : moment();
  }
}
